/**
 * 统一邮编管理前端接口
 * 支持中国4级层级（省/市/区县/街道）和美国2级层级（州/市）
 * @author ChaseZhang
 * @since 2025-01-15
 */

import { httpClient as http } from '@/utils/http'
import type { PageRequest } from '@/types/api'

// API基础路径
const API_BASE = '/mdm/postCode'

// ==================== 通用类型定义 ====================

// 分页响应类型
export interface PageResponse<T> {
  pageNum: number // 当前页码
  pageSize: number // 每页大小
  total: number // 总记录数
  pages: number // 总页数
  records: T[] // 数据列表
}

// API统一响应格式
export interface ApiResponse<T> {
  code: number
  message: string
  data: T
  success: boolean
}

// 国家ID类型，实际值为 dmn_country.id
export type CountryType = number

// 启用状态枚举
export enum EnabledStatus {
  DISABLED = 0, // 禁用
  ENABLED = 1 // 启用
}

// ==================== 邮编相关类型定义 ====================

// 邮编分页查询请求
export interface PostCodePageRequest extends PageRequest {
  keyword?: string // 关键词搜索
  postalCode?: string // 邮政编码
  countryId?: number // 国家ID
  provinceId?: string // 省/州ID
  cityId?: string // 市ID
  districtId?: string // 区县ID（仅中国）
  streetId?: string // 街道ID（仅中国）
  areaName?: string // 区域名称
  enabled?: EnabledStatus // 启用状态
  tenantId?: string // 租户ID
}

// 邮编列表项视图对象
export interface PostCodeListVO {
  id: string // 主键ID
  tenantId?: string // 租户ID
  countryId: number // 国家ID
  countryName?: string // 国家名称
  postalCode: string // 邮政编码
  // 行政区划信息
  provinceId?: string // 省/州ID
  provinceName?: string // 省/州名称
  cityId?: string // 市ID
  cityName?: string // 市名称
  districtId?: string // 区县ID（仅中国）
  districtName?: string // 区县名称（仅中国）
  streetId?: string // 街道ID（仅中国）
  streetName?: string // 街道名称（仅中国）
  // 区域信息
  areaName?: string // 区域名称
  areaNameEn?: string // 区域英文名称
  enabled: EnabledStatus // 启用状态
  dateCreated?: string // 创建时间
  dateModified?: string // 修改时间
}

// 邮编详情视图对象
export interface PostCodeVO {
  id: string // 主键ID
  tenantId?: string // 租户ID
  countryId: number // 国家ID
  countryName?: string // 国家名称
  postalCode: string // 邮政编码
  // 行政区划信息
  provinceId?: string // 省/州ID
  provinceName?: string // 省/州名称
  cityId?: string // 市ID
  cityName?: string // 市名称
  districtId?: string // 区县ID（仅中国）
  districtName?: string // 区县名称（仅中国）
  streetId?: string // 街道ID（仅中国）
  streetName?: string // 街道名称（仅中国）
  // 区域信息
  areaName?: string // 区域名称
  areaNameEn?: string // 区域英文名称
  enabled: EnabledStatus // 启用状态
  dateCreated?: string // 创建时间
  dateModified?: string // 修改时间
}

// 邮编保存请求
export interface PostCodeSaveRequest {
  postalCode: string // 邮政编码
  countryId: number // 国家ID
  // 行政区划ID
  provinceId?: string // 省/州ID
  cityId?: string // 市ID
  districtId?: string // 区县ID（仅中国）
  streetId?: string // 街道ID（仅中国）
  // 区域信息
  areaName?: string // 区域名称
  areaNameEn?: string // 区域英文名称
  enabled?: EnabledStatus // 启用状态
  tenantId?: string // 租户ID
}

// 邮编更新请求
export interface PostCodeUpdateRequest extends PostCodeSaveRequest {
  id: string // 主键ID
}

// 模糊搜索请求
export interface PostCodeSearchRequest {
  keyword: string // 关键词
  countryId?: number // 国家ID
  enabled?: EnabledStatus // 启用状态
  tenantId?: string // 租户ID
}

// 批量删除请求
export interface BatchDeleteRequest {
  ids: string[]
}

// 批量状态更新请求
export interface BatchUpdateStatusRequest {
  ids: string[]
  enabled: EnabledStatus
}

// ==================== 国家ID动态获取 ====================

// 缓存国家ID
const countryIdCache = new Map<string, number>()

/**
 * 根据国家代码获取国家ID
 * @param countryCode 国家代码（如 'CN', 'US'）
 * @param defaultId 备用ID
 */
const getCountryIdByCode = async(countryCode: string, defaultId: number): Promise<number> => {
  if (countryIdCache.has(countryCode)) {
    return countryIdCache.get(countryCode)!
  }

  try {
    const response = await http.post('/mdm/universal-search/search', {
      dataType: 'country',
      keyword: countryCode,
      pageSize: 1,
      pageNum: 1
    })
    const countries = (response as any).data || response
    if (countries && countries.length > 0) {
      const countryId = Number(countries[0].id)
      countryIdCache.set(countryCode, countryId)
      return countryId
    }
  } catch (error) {
    console.error(`获取 ${countryCode} 国家ID失败，使用默认ID: ${defaultId}`, error)
  }

  // 如果接口失败，使用并缓存备用ID
  countryIdCache.set(countryCode, defaultId)
  return defaultId
}

/**
 * 根据ID列表批量获取运输条款信息
 * @param ids 运输条款ID列表
 */
export const getTransportTermsByIds = async (ids: string[]): Promise<any> => {
  if (!ids || ids.length === 0) {
    return []
  }

  try {
    const response = await http.post('/mdm/universal-search/search', {
      dataType: 'transport_term',
      ids
    })
    return response
  } catch (error) {
    console.error('获取运输条款信息失败', error)
    return []
  }
}

/**
 */
const getChinaCountryId = async(): Promise<number> => {
  return getCountryIdByCode('CN', 862)
}

/**
 * 获取美国国家ID
 */
const getUSACountryId = async(): Promise<number> => {
  return getCountryIdByCode('US', 877)
}

// ==================== API客户端对象 ====================

// 统一邮编API
export const postCodeApi = {
  // 分页查询邮编
  page: (params: PostCodePageRequest) => {
    return http.post<PageResponse<PostCodeListVO>>(`${API_BASE}/queryPage`, params)
  },

  // 查询邮编列表（不分页）
  list: (params: PostCodePageRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/queryList`, params)
  },

  // 模糊搜索邮编
  search: (params: PostCodeSearchRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/search`, params)
  },

  // 获取邮编详情
  getById: (params: { id: string }) => {
    return http.get<PostCodeVO>(`${API_BASE}/getById/${params.id}`)
  },

  // 新增邮编
  save: (params: PostCodeSaveRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 更新邮编
  update: (params: PostCodeUpdateRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 删除邮编
  delete: (params: { id: string }) => {
    return http.delete<boolean>(`${API_BASE}/delete/${params.id}`)
  },

  // 批量删除邮编
  batchDelete: (ids: string[]) => {
    return http.post<boolean>(`${API_BASE}/deleteBatch`, ids)
  },

  // 更新邮编状态（启用/禁用）
  updateStatus: (id: string, enabled: EnabledStatus) => {
    return http.post<boolean>(`${API_BASE}/updateStatus?id=${id}&enabled=${enabled}`)
  },

  // 批量更新邮编状态（启用/禁用）
  batchUpdateStatus: (ids: string[], enabled: EnabledStatus) => {
    return http.post<boolean>(`${API_BASE}/batchUpdateStatus`, ids, {
      params: { enabled }
    })
  },

  // 根据邮政编码查询
  queryByPostalCode: (params: PostCodePageRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/queryByPostalCode`, params)
  },

  // 根据国家查询邮编
  queryByCountry: (params: PostCodePageRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/queryByCountry`, params)
  },

  // 根据省/州查询邮编
  queryByProvince: (params: PostCodePageRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/queryByProvince`, params)
  },

  // 根据市查询邮编
  queryByCity: (params: PostCodePageRequest) => {
    return http.post<PostCodeListVO[]>(`${API_BASE}/queryByCity`, params)
  },

  // 根据邮编获取完整地址信息
  getAddressByPostalCode: (postalCode: string) => {
    return http.get<PostCodeListVO[]>(`${API_BASE}/getAddressByPostalCode/${postalCode}`)
  },

  // 验证邮编是否存在
  exists: (params: { postalCode: string; countryId?: number }) => {
    const queryParams = new URLSearchParams()
    queryParams.append('postalCode', params.postalCode)
    if (params.countryId) queryParams.append('countryId', params.countryId.toString())
    return http.get<boolean>(`${API_BASE}/exists?${queryParams.toString()}`)
  }
}

// ==================== 中国邮编便捷接口 ====================

// 中国邮编专用API（向后兼容）
export const chinaPostCodeApi = {
  // 分页查询中国邮编
  page: async(params: Omit<PostCodePageRequest, 'countryId'>) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.page({ ...params, countryId })
  },

  // 查询中国邮编列表
  list: async(params: Omit<PostCodePageRequest, 'countryId'>) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.list({ ...params, countryId })
  },

  // 模糊搜索中国邮编
  search: async(params: Omit<PostCodeSearchRequest, 'countryId'>) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.search({ ...params, countryId })
  },

  // 获取中国邮编详情
  getById: (params: { id: string }) => {
    return postCodeApi.getById(params)
  },

  // 新增中国邮编
  save: async(params: Omit<PostCodeSaveRequest, 'countryId'>) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.save({ ...params, countryId })
  },

  // 更新中国邮编
  update: async(params: Omit<PostCodeUpdateRequest, 'countryId'>) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.update({ ...params, countryId })
  },

  // 删除中国邮编
  delete: (params: { id: string }) => {
    return postCodeApi.delete(params)
  },

  // 批量删除中国邮编
  batchDelete: (ids: string[]) => {
    return postCodeApi.batchDelete(ids)
  },

  // 更新中国邮编状态
  updateStatus: (id: string, enabled: EnabledStatus) => {
    return postCodeApi.updateStatus(id, enabled)
  },

  // 批量更新中国邮编状态
  batchUpdateStatus: (ids: string[], enabled: EnabledStatus) => {
    return postCodeApi.batchUpdateStatus(ids, enabled)
  },

  // 根据邮政编码查询中国邮编
  getByPostalCode: async(params: { postalCode: string }) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.queryByPostalCode({
      postalCode: params.postalCode,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据省份查询中国邮编
  queryByProvince: async(params: { provinceId: string }) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.queryByProvince({
      provinceId: params.provinceId,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据城市查询中国邮编
  queryByCity: async(params: { cityId: string }) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.queryByCity({
      cityId: params.cityId,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据区县查询中国邮编
  queryByDistrict: async(params: { districtId: string }) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.list({
      districtId: params.districtId,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据行政区划组合查询中国邮编
  queryByDivisionCombo: async(params: {
    provinceId?: string
    cityId?: string
    districtId?: string
    streetId?: string
  }) => {
    const countryId = await getChinaCountryId()
    return postCodeApi.list({
      ...params,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  }
}

// ==================== 美国邮编便捷接口 ====================

// 美国邮编专用API（向后兼容）
export const usPostCodeApi = {
  // 分页查询美国邮编
  page: async(params: Omit<PostCodePageRequest, 'countryId'>) => {
    const countryId = await getUSACountryId()
    return postCodeApi.page({ ...params, countryId })
  },

  // 查询美国邮编列表
  list: async(params: Omit<PostCodePageRequest, 'countryId'>) => {
    const countryId = await getUSACountryId()
    return postCodeApi.list({ ...params, countryId })
  },

  // 模糊搜索美国邮编
  search: async(params: Omit<PostCodeSearchRequest, 'countryId'>) => {
    const countryId = await getUSACountryId()
    return postCodeApi.search({ ...params, countryId })
  },

  // 获取美国邮编详情
  getById: (params: { id: string }) => {
    return postCodeApi.getById(params)
  },

  // 新增美国邮编
  save: async(params: Omit<PostCodeSaveRequest, 'countryId'>) => {
    const countryId = await getUSACountryId()
    return postCodeApi.save({ ...params, countryId })
  },

  // 更新美国邮编
  update: async(params: Omit<PostCodeUpdateRequest, 'countryId'>) => {
    const countryId = await getUSACountryId()
    return postCodeApi.update({ ...params, countryId })
  },

  // 删除美国邮编
  delete: (params: { id: string }) => {
    return postCodeApi.delete(params)
  },

  // 批量删除美国邮编
  batchDelete: (ids: string[]) => {
    return postCodeApi.batchDelete(ids)
  },

  // 更新美国邮编状态
  updateStatus: (id: string, enabled: EnabledStatus) => {
    return postCodeApi.updateStatus(id, enabled)
  },

  // 批量更新美国邮编状态
  batchUpdateStatus: (ids: string[], enabled: EnabledStatus) => {
    return postCodeApi.batchUpdateStatus(ids, enabled)
  },

  // 根据邮政编码查询美国邮编
  getByPostalCode: async(params: { postalCode: string }) => {
    const countryId = await getUSACountryId()
    return postCodeApi.queryByPostalCode({
      postalCode: params.postalCode,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据州查询美国邮编（通过provinceId）
  queryByState: async(params: { stateId: string }) => {
    const countryId = await getUSACountryId()
    return postCodeApi.queryByProvince({
      provinceId: params.stateId,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据城市查询美国邮编
  queryByCity: async(params: { cityId: string }) => {
    const countryId = await getUSACountryId()
    return postCodeApi.queryByCity({
      cityId: params.cityId,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 根据区域查询美国邮编
  queryByArea: async(params: { areaName: string }) => {
    const countryId = await getUSACountryId()
    return postCodeApi.list({
      areaName: params.areaName,
      countryId,
      pageNum: 1,
      pageSize: 100
    })
  },

  // 验证美国邮编是否存在
  exists: async(params: { postalCode: string }) => {
    const countryId = await getUSACountryId()
    return postCodeApi.exists({
      postalCode: params.postalCode,
      countryId
    })
  }
}

// ==================== 类型别名（向后兼容） ====================

// 中国邮编类型别名
export type ChinaPostalCodeListVO = PostCodeListVO
export type ChinaPostalCodeVO = PostCodeVO
export type ChinaPostalCodePageRequest = Omit<PostCodePageRequest, 'countryId'>
export type ChinaPostalCodeSaveRequest = Omit<PostCodeSaveRequest, 'countryId'>
export type ChinaPostalCodeUpdateRequest = Omit<PostCodeUpdateRequest, 'countryId'>
export type ChinaPostalCodeSearchRequest = Omit<PostCodeSearchRequest, 'countryId'>

// 美国邮编类型别名
export type UsPostalCodeListVO = PostCodeListVO
export type UsPostalCodeVO = PostCodeVO
export type UsPostalCodePageRequest = Omit<PostCodePageRequest, 'countryId'>
export type UsPostalCodeSaveRequest = Omit<PostCodeSaveRequest, 'countryId'>
export type UsPostalCodeUpdateRequest = Omit<PostCodeUpdateRequest, 'countryId'>
export type UsPostalCodeSearchRequest = Omit<PostCodeSearchRequest, 'countryId'>

// 向后兼容的分页响应类型
export type PageResultVO<T> = PageResponse<T>
